import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import api from "@/lib/api";
import { Users, ClipboardList, FileVideo, ArrowRight, Gift, Clock, CheckCircle2, Sparkles } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ families: 0, children: 0, sessionsInProgress: 0 });
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [tsCount, setTsCount] = useState({ pending: 0, total: 0 });
  const [tsLoading, setTsLoading] = useState(true);

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantSlots, setGrantSlots] = useState(0);
  const [grantNote, setGrantNote] = useState("");
  const [grantResult, setGrantResult] = useState(null);
  const [grantErr, setGrantErr] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);

  const loadPending = () => {
    setPendingLoading(true);
    api.get("/admin/pending-purchases")
      .then(({ data }) => setPending(data?.items || []))
      .catch(() => setPending([]))
      .finally(() => setPendingLoading(false));
  };

  useEffect(() => {
    api.get("/admin/families").then(({ data }) => {
      const families = new Set(data.map((c) => c.parent?.email).filter(Boolean)).size;
      const inProgress = data.filter((c) => c.current_session > 0 && c.current_session <= 12).length;
      setStats({ families, children: data.length, sessionsInProgress: inProgress });
      setLoading(false);
    }).catch(() => setLoading(false));
    loadPending();
    api.get("/admin/thrive-stories/pending-count")
      .then(({ data }) => setTsCount({ pending: Number(data?.pending || 0), total: Number(data?.total || 0) }))
      .catch(() => setTsCount({ pending: 0, total: 0 }))
      .finally(() => setTsLoading(false));
  }, []);

  const openGrant = (prefillEmail = "") => {
    setGrantEmail(prefillEmail);
    setGrantSlots(0);
    setGrantNote("");
    setGrantResult(null);
    setGrantErr("");
    setGrantOpen(true);
  };

  const submitGrant = async () => {
    setGrantErr(""); setGrantBusy(true); setGrantResult(null);
    try {
      const { data } = await api.post("/admin/grant-free-access", {
        email: grantEmail.trim(),
        addon_slots: Number(grantSlots) || 0,
        note: grantNote.trim(),
      });
      setGrantResult(data);
      loadPending();
    } catch (e) {
      setGrantErr(e?.response?.data?.detail || "Grant failed");
    } finally { setGrantBusy(false); }
  };

  return (
    <AdminShell subtitle="Admin overview" title="Operations console">
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="rounded-2xl bg-[#151737] border border-white/10 p-6" data-testid="admin-stat-families">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-semibold">Families</div>
            <Users size={16} className="text-amber-300" />
          </div>
          <div className="mt-3 font-display text-4xl font-bold text-white">{loading ? "…" : stats.families}</div>
          <div className="text-xs text-slate-400 mt-1">Parents with at least one enrolled child</div>
        </div>
        <div className="rounded-2xl bg-[#151737] border border-white/10 p-6" data-testid="admin-stat-children">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-semibold">Children enrolled</div>
            <ClipboardList size={16} className="text-amber-300" />
          </div>
          <div className="mt-3 font-display text-4xl font-bold text-white">{loading ? "…" : stats.children}</div>
          <div className="text-xs text-slate-400 mt-1">Across all tribes</div>
        </div>
        <div className="rounded-2xl bg-[#151737] border border-white/10 p-6" data-testid="admin-stat-in-progress">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-semibold">In-progress journeys</div>
            <ClipboardList size={16} className="text-amber-300" />
          </div>
          <div className="mt-3 font-display text-4xl font-bold text-white">{loading ? "…" : stats.sessionsInProgress}</div>
          <div className="text-xs text-slate-400 mt-1">Currently between sessions 1 and 12</div>
        </div>
      </div>

      <div className="rounded-2xl bg-[#151737] border border-white/10 p-6 mb-8" data-testid="admin-pending-purchases">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 text-amber-300">
              <Clock size={16} />
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold">Paid but not registered</div>
            </div>
            <div className="mt-1 font-display text-xl font-bold text-white">
              {pendingLoading ? "…" : `${pending.length} pending signup${pending.length === 1 ? "" : "s"}`}
            </div>
            <div className="text-xs text-slate-400 mt-1">Kajabi credits waiting — they'll auto-apply the moment the buyer clicks "Register"</div>
          </div>
          <button
            data-testid="admin-grant-open"
            onClick={() => openGrant("")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
          >
            <Gift size={14} /> Grant free access
          </button>
        </div>

        {pendingLoading ? (
          <div className="text-xs text-slate-500 py-3">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="text-xs text-slate-500 py-3">Nobody is waiting. Every buyer has registered.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="text-left py-2 pr-3">Email</th>
                  <th className="text-left py-2 pr-3">Access</th>
                  <th className="text-left py-2 pr-3">Sibling slots paid</th>
                  <th className="text-left py-2 pr-3">First paid</th>
                  <th className="text-left py-2 pr-3">Registered?</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((row) => (
                  <tr key={row.email} className="border-b border-white/5" data-testid={`admin-pending-row-${row.email}`}>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-200">{row.email}</td>
                    <td className="py-2 pr-3">
                      {row.main > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-emerald-500/15 text-emerald-300 font-semibold">FULL</span>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-slate-200">{row.addon_slots}</td>
                    <td className="py-2 pr-3 text-xs text-slate-400">{(row.first_credit_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 pr-3">
                      {row.registered ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-300"><CheckCircle2 size={11} /> yes (credit stalled)</span>
                      ) : (
                        <span className="text-[10px] text-slate-500">not yet</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Link
        to="/admin/thrive-stories"
        data-testid="admin-thrive-pending-card"
        className={`block rounded-2xl border p-6 mb-8 transition-all ${tsCount.pending > 0 ? "bg-gradient-to-br from-[#2a1a3a] to-[#151737] border-amber-300/40 hover:border-amber-300" : "bg-[#151737] border-white/10 hover:border-white/20"}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-amber-300">
              <Sparkles size={16} />
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold">Thrive Story personalization</div>
            </div>
            <div className="mt-1 font-display text-xl font-bold text-white" data-testid="admin-thrive-pending-count">
              {tsLoading
                ? "…"
                : tsCount.pending > 0
                  ? `${tsCount.pending} child${tsCount.pending === 1 ? "" : "ren"} waiting for personalization`
                  : tsCount.total > 0
                    ? "All Thrive Stories published ✨"
                    : "No Thrive Story enrollments yet"}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {tsCount.pending > 0
                ? "Upload companion photos + personalized certs, then publish — the parent gets an email the moment you do."
                : `${tsCount.total} total Thrive Story child${tsCount.total === 1 ? "" : "ren"} enrolled to date.`}
            </div>
          </div>
          <ArrowRight size={16} className="text-slate-500 ml-4" />
        </div>
      </Link>

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/admin/families" data-testid="admin-tile-families" className="group rounded-2xl bg-[#151737] border border-white/10 p-6 hover:border-amber-300 transition-all">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-lg bg-amber-300/10 flex items-center justify-center"><Users size={18} className="text-amber-300" /></div>
            <ArrowRight size={16} className="text-slate-500 group-hover:text-amber-300 transition-colors" />
          </div>
          <div className="mt-4 font-display text-xl font-bold text-white">Family lookup &amp; progress audit</div>
          <p className="mt-1 text-sm text-slate-400">Search every enrolled family. Drill into any child for a full, dated audit of enrollment, sessions, check-ins, roadblocks, certificates, and donations — the source of truth for refund decisions.</p>
        </Link>
        <Link to="/admin/content" data-testid="admin-tile-content" className="group rounded-2xl bg-[#151737] border border-white/10 p-6 hover:border-amber-300 transition-all">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-lg bg-amber-300/10 flex items-center justify-center"><FileVideo size={18} className="text-amber-300" /></div>
            <ArrowRight size={16} className="text-slate-500 group-hover:text-amber-300 transition-colors" />
          </div>
          <div className="mt-4 font-display text-xl font-bold text-white">Content management</div>
          <p className="mt-1 text-sm text-slate-400">Paste real Kajabi video URLs or iframe embed codes into every placeholder and edit written copy inline — sessions, symptoms, tribes, and education modules.</p>
        </Link>
      </div>

      {grantOpen && (
        <div data-testid="admin-grant-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-[#151737] border border-emerald-500/40 p-6 space-y-4">
            <div className="flex items-center gap-2 text-emerald-300">
              <Gift size={20} />
              <div className="font-display text-xl font-bold">Grant free access</div>
            </div>
            <div className="text-sm text-slate-300 space-y-2">
              <p>Give someone the full Breathe to Thrive program (and optional sibling slots) without a Kajabi purchase. Works for both new emails (queues credits for their signup) and existing accounts (applies right away).</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Email address</label>
                <input
                  data-testid="admin-grant-email"
                  type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
                  placeholder="mom@example.com"
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0F1020] border border-white/10 focus:border-emerald-400 focus:outline-none text-sm text-slate-100 placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Extra sibling slots (beyond the first free child)</label>
                <input
                  data-testid="admin-grant-slots"
                  type="number" min="0" max="10" value={grantSlots}
                  onChange={(e) => setGrantSlots(e.target.value)}
                  className="mt-1 w-32 px-3 py-2 rounded-lg bg-[#0F1020] border border-white/10 focus:border-emerald-400 focus:outline-none text-sm text-slate-100"
                />
                <div className="text-[10px] text-slate-500 mt-1">Set 0 for a single-child family, 2 to enable up to 3 kids total, etc.</div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Note (audit trail)</label>
                <input
                  data-testid="admin-grant-note"
                  type="text" value={grantNote} onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="e.g. Compensated participant, refund credit, VIP"
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0F1020] border border-white/10 focus:border-emerald-400 focus:outline-none text-sm text-slate-100 placeholder-slate-500"
                />
              </div>
            </div>
            {grantErr && <div className="text-xs text-rose-300" data-testid="admin-grant-error">{grantErr}</div>}
            {grantResult && (
              <div data-testid="admin-grant-success" className="text-xs bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-emerald-200">
                <div className="font-semibold mb-1">✓ Access granted to {grantResult.email}</div>
                <div>Main enrollment: {grantResult.main?.queued ? "queued (will apply on signup)" : "applied to existing account"}</div>
                {grantResult.addon_slots > 0 && (
                  <div>Sibling slots: +{grantResult.addon_slots} {grantResult.addon?.queued ? "queued" : "applied"}</div>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                data-testid="admin-grant-cancel"
                onClick={() => setGrantOpen(false)} disabled={grantBusy}
                className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5"
              >Close</button>
              <button
                data-testid="admin-grant-submit"
                onClick={submitGrant}
                disabled={!grantEmail.trim() || grantBusy}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500 text-white hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed"
              >{grantBusy ? "Granting…" : "Grant access"}</button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
